import { useMutation, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { axiosInstance } from '@/modules/shared/lib/axios'
import { QUERY_KEY_SETTINGS_ADMIN_PANEL } from '@/lib/api/constants'
import { UpdateUserRolesPayload } from './types'

type UpdateUserRolesResponse = {
  success?: boolean
  Success?: boolean // C# serialization might use PascalCase
  code?: string
  Code?: string
  message?: string
  Message?: string
  path?: string
  data?: {
    message?: string
  }
}

export class UpdateUserRolesError extends Error {
  code: string

  constructor(code: string, message: string) {
    super(message)
    this.code = code
    this.name = 'UpdateUserRolesError'
  }
}

function extractErrorFromResponse(data: UpdateUserRolesResponse | undefined): { code?: string; message?: string } {
  if (!data) return {}
  return {
    code: data.code || data.Code,
    message: data.message || data.Message,
  }
}

async function updateUserRoles(companyId: string, payload: UpdateUserRolesPayload) {
  const { user, roles } = payload

  // Find the roles for the current company
  const currentCompanyRoles = roles.find((r) => String(r.companyId) === String(companyId))
  const roleIds = currentCompanyRoles?.roles.map((role) => ({ id: role.id })) ?? []

  try {
    // Use the company-scoped endpoint for updating roles
    const { data } = await axiosInstance.post<UpdateUserRolesResponse>(
      `/config-api/${companyId}/configuration/admin/users/${user.id}`,
      {
        roles: roleIds,
      }
    )

    // Backend might return 200 with error details
    const { code, message } = extractErrorFromResponse(data)
    const success = data.success ?? data.Success

    if (success === false || code) {
      throw new UpdateUserRolesError(code || 'UNKNOWN_ERROR', message || 'Unknown error')
    }

    return data
  } catch (error) {
    // Re-throw UpdateUserRolesError as-is
    if (error instanceof UpdateUserRolesError) {
      throw error
    }
    // Handle axios errors (4xx, 5xx responses)
    if (axios.isAxiosError(error) && error.response?.data) {
      const responseData = error.response.data as UpdateUserRolesResponse
      const { code, message } = extractErrorFromResponse(responseData)
      if (code || message) {
        throw new UpdateUserRolesError(code || 'API_ERROR', message || error.message)
      }
    }
    // Re-throw other errors
    throw error
  }
}

export function useMutationUpdateUserRoles(companyId: string) {
  const queryClient = useQueryClient()

  return useMutation<UpdateUserRolesResponse, UpdateUserRolesError | Error, UpdateUserRolesPayload>({
    mutationFn: (payload) => updateUserRoles(companyId, payload),
    onSuccess: () => {
      // Invalidate the users query to refetch the updated data
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_SETTINGS_ADMIN_PANEL, 'users', companyId],
      })
    },
  })
}

